import React, { useState } from 'react';
import ActionButton from '../../../../Components/Common/ActionButton';
import { FormActions, TextField } from '../../../../Components/Form';
import useAsyncAction from '../../../../Components/Hooks/useAsyncAction';
import { confirmAction } from '../../../../utils/confirm';
import notify from '../../../../utils/notify';
import { formatPrice } from '../../utils/formatters';
import { lineMitraLastOffer, lineOwnerEstimate } from '../../utils/roomSpec';
import {
  activeRounds,
  getLinePermissions,
  PARTNER_STATUS,
  SENDER_SIDE,
  validateRoundPrice,
  withdrawRoundSecondsLeft,
} from '../../utils/negotiationStatus';
import { copy, isBuyerLocale } from '../../utils/negotiationCopy';
import PriceDealBanner from './PriceDealBanner';

const askConfirm = (c, tone = 'primary') =>
  confirmAction({
    tone,
    title: c.title,
    message: c.message,
    subject: c.subject,
    confirmLabel: c.confirm,
    cancelLabel: c.cancel,
    reasonLabel: c.reasonLabel,
    reasonPlaceholder: c.reasonPlaceholder,
  });

const lastBy = (rounds, side) =>
  [...rounds].reverse().find(r => r.senderSide === side);

const PriceCard = ({ label, value, tone }) => (
  <div className={`neg-price-card${tone ? ` neg-price-card--${tone}` : ''}`}>
    <div className='neg-price-card__label'>{label}</div>
    <div className='neg-price-card__value'>{value}</div>
  </div>
);

const AdjustPanel = ({
  cargo,
  partner,
  line,
  mode = 'buyer',
  nowMs,
  unit,
  onSubmitRound,
  onAccept,
  onWithdrawRound,
}) => {
  const isSupplier = mode === 'supplier';
  const locale = isBuyerLocale(mode) ? 'id' : 'en';
  const role = isSupplier ? 'seller' : 'buyer';
  const rounds = activeRounds(line);
  const last = rounds[rounds.length - 1];
  const lastBuyer = lastBy(rounds, SENDER_SIDE.PERTAMINA);
  const lastSeller = lastBy(rounds, SENDER_SIDE.MITRA);
  const otherLast = isSupplier ? lastBuyer : lastSeller;
  const ownLast = isSupplier ? lastSeller : lastBuyer;
  const isAccepted = line?.statusLabel === PARTNER_STATUS.ACCEPTED;
  const { live, myTurn, canRound, canAccept, canWithdrawRound } =
    getLinePermissions({ cargo, partner, line, mode, nowMs });
  const withdrawSecondsLeft = withdrawRoundSecondsLeft(line, nowMs);
  const withdrawableRound = (line?.rounds || []).find(
    r => String(r.id) === String(line?.withdrawableRoundId)
  );
  const mitraLastOffer = lineMitraLastOffer(line);
  const ownerEstimate = lineOwnerEstimate(line);
  const fmt = value => formatPrice(value, unit);
  const buyerTarget =
    lastBuyer?.price == null ? 'Pending' : fmt(lastBuyer.price);
  const improveLabel = isAccepted
    ? isSupplier
      ? 'Accepted price'
      : 'Accepted offer'
    : isSupplier
      ? 'Your improve'
      : 'Seller improve';
  const improveValue = fmt(isAccepted ? line?.acceptedPrice : mitraLastOffer);

  const [price, setPrice] = useState('');
  const [seedKey, setSeedKey] = useState('');
  const nextSeed = `${line?.id ?? ''}:${ownLast?.id ?? ''}`;
  if (nextSeed !== seedKey) {
    setSeedKey(nextSeed);
    setPrice(ownLast?.price != null ? String(ownLast.price) : '');
  }

  const priceError =
    canRound && price.trim()
      ? validateRoundPrice(price, mitraLastOffer, fmt).error
      : null;
  const fieldMode = isAccepted
    ? 'readonly'
    : canRound
      ? 'editable'
      : 'disabled';
  const shownPrice = canRound
    ? price
    : ownLast?.price != null
      ? String(ownLast.price)
      : '';

  const [handleSubmit, isSubmitting] = useAsyncAction(async () => {
    if (!canRound) return;
    const { price: nextPrice, error } = validateRoundPrice(
      price,
      mitraLastOffer,
      fmt
    );
    if (error) {
      notify.warning(error);
      return;
    }
    const c = copy.confirmSubmit(
      formatPrice(nextPrice, unit),
      formatPrice(otherLast?.price, unit),
      locale,
      role
    );
    if (!(await askConfirm(c))) return;

    try {
      await onSubmitRound(line.id, { price: nextPrice });
      setPrice('');
      notify.success(
        isSupplier
          ? 'Counter offer submitted successfully.'
          : 'Target price submitted successfully.'
      );
    } catch (error) {
      notify.apiError(error, isSupplier ? 'submit counter' : 'submit target');
    }
  });

  const [handleAccept, isAccepting] = useAsyncAction(async () => {
    if (!canAccept) return;
    const acceptPrice = rounds.length ? last?.price : mitraLastOffer;
    const c = copy.confirmAccept(formatPrice(acceptPrice, unit), locale, role);
    if (!(await askConfirm(c, 'warning'))) return;
    try {
      await onAccept(line.id);
      notify.success('Price accepted successfully.');
    } catch (error) {
      notify.apiError(error, 'accept the price');
    }
  });

  const [handleWithdrawRound, isWithdrawingRound] = useAsyncAction(async () => {
    if (!canWithdrawRound) return;
    const c = copy.confirmWithdrawRound(
      `${line.label} · ${fmt(withdrawableRound?.price)}`,
      withdrawableRound?.roundNo ?? ''
    );
    if (!(await askConfirm(c, 'warning'))) return;
    try {
      await onWithdrawRound(line.id, line.withdrawableRoundId);
      notify.success('Round withdrawn.');
    } catch (error) {
      notify.apiError(error, 'withdraw the round');
    }
  });

  const busy = isSubmitting || isAccepting || isWithdrawingRound;

  const description = isAccepted
    ? 'Closed — price accepted'
    : !live
      ? 'Closed — no further proposals on this line'
      : isSupplier
        ? canRound
          ? 'Buyer sent a target — submit a counter or accept the target'
          : myTurn
            ? 'Waiting for Pertamina to open Round 1'
            : 'Counter sent — waiting for Pertamina'
        : canRound
          ? rounds.length
            ? 'Seller replied — accept or send a new target'
            : 'Set your target or accept the seller improve price'
          : 'Waiting for seller';

  const showField = live || isAccepted;
  const awaitingBuyerTarget = isSupplier && live && !lastBuyer && !isAccepted;

  return (
    <section className='neg-panel h-100'>
      <header className='neg-panel__head'>
        <div>
          <h3 className='neg-panel__title'>
            <i
              className={
                isAccepted
                  ? 'ri-checkbox-circle-line'
                  : isSupplier
                    ? 'ri-store-2-line'
                    : 'ri-funds-box-line'
              }
              aria-hidden='true'
            />
            Adjust negotiation parameters &amp; counter form
          </h3>
          {line?.label && (
            <p className='neg-panel__desc fw-semibold text-body'>
              {line.label}
            </p>
          )}
          <p className='neg-panel__desc'>{description}</p>
        </div>
      </header>
      <div className='neg-panel__body'>
        <div className='neg-price-grid'>
          {isSupplier ? (
            <PriceCard label='Buyer target' value={buyerTarget} tone='accent' />
          ) : (
            <PriceCard
              label='Owner Estimate / HPS'
              value={fmt(ownerEstimate)}
            />
          )}
          <PriceCard
            label={improveLabel}
            value={improveValue}
            tone={isAccepted ? 'success' : isSupplier ? undefined : 'accent'}
          />
        </div>

        {showField && (
          <TextField
            label={
              isAccepted
                ? isSupplier
                  ? 'Final counter'
                  : 'Final buyer target'
                : isSupplier
                  ? 'Your counter offer'
                  : 'Your target price'
            }
            name='roundPrice'
            type='text'
            inputMode='decimal'
            value={shownPrice}
            onChange={e => setPrice(e.target.value)}
            placeholder={isSupplier ? 'e.g. 0.60' : 'e.g. 0.55'}
            suffix={unit}
            hint={
              canRound && mitraLastOffer != null
                ? `Must be lower than the Mitra Last Offer (${fmt(mitraLastOffer)}).`
                : undefined
            }
            error={priceError || undefined}
            mode={busy ? 'disabled' : fieldMode}
            required={canRound}
          />
        )}
        {awaitingBuyerTarget && (
          <p className='neg-hint'>
            Pertamina has not submitted a Round 1 target yet.
          </p>
        )}

        {live && (
          <FormActions align='start' className='neg-actions-stack mt-3'>
            {canRound && canAccept && (
              <ActionButton
                variant='secondary'
                type='button'
                icon='ri-hand-heart-line'
                loading={isAccepting}
                disabled={busy}
                onClick={handleAccept}
              >
                Accept offer
              </ActionButton>
            )}
            {canRound && (
              <ActionButton
                variant='primary'
                type='button'
                icon='ri-send-plane-line'
                loading={isSubmitting}
                disabled={busy}
                onClick={handleSubmit}
              >
                {isSupplier ? 'Submit counter' : 'Submit target'}
              </ActionButton>
            )}
            {!canRound && canWithdrawRound && (
              <ActionButton
                variant='secondary'
                type='button'
                icon='ri-arrow-go-back-line'
                className={isSupplier ? 'w-100' : undefined}
                loading={isWithdrawingRound}
                disabled={busy}
                onClick={handleWithdrawRound}
              >
                {`Withdraw (${withdrawSecondsLeft}s)`}
              </ActionButton>
            )}
            {!canRound && !canWithdrawRound && (
              <ActionButton
                variant='secondary'
                type='button'
                icon='ri-hourglass-line'
                disabled
              >
                {isSupplier
                  ? myTurn
                    ? 'Waiting buyer target'
                    : 'Waiting buyer'
                  : 'Waiting seller'}
              </ActionButton>
            )}
          </FormActions>
        )}

        {isAccepted && (
          <PriceDealBanner price={fmt(line.acceptedPrice)} mode={mode} />
        )}
      </div>
    </section>
  );
};

export default AdjustPanel;
